import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  SvgIcon,
  List,
  ListItemButton,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Facebook,
  Instagram,
  LinkedIn,
  LinkOff,
  Twitter,
  YouTube,
  Article,
  Business,
  Email,
  Phone,
  Work,
  Archive,
  Unarchive,
  PhotoLibrary,
  SettingsSuggest,
  ManageSearch,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { clientsAPI, gscAPI } from '../services/api';
import GbpConnectionCard from './clients/GbpConnectionCard';
import PlatformAccountsSection from '../components/PlatformAccountsSection';
import ClientReadinessPanel from '../components/ClientReadinessPanel';
import FacebookPageSelector from '../components/FacebookPageSelector';
import { linkedinAPI } from '../services/api';
import { xAPI } from '../services/api';
import { pinterestAPI } from '../services/api';
import { youtubeAPI } from '../services/api';
import { tumblrAPI } from '../services/api';
import { instagramAPI } from '../services/api';
import { SHOW_STANDALONE_INSTAGRAM_CONNECT } from '../config/featureFlags';
import { facebookAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import { DataTable, PageHeader } from '../components/primitives';

// Pinterest brand icon (MUI has no built-in Pinterest icon)
function PinterestIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.214 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"
      />
    </SvgIcon>
  );
}

function formatGscCtrRatio(ratio) {
  if (ratio == null || Number.isNaN(ratio)) return '—';
  return `${(Number(ratio) * 100).toFixed(2)}%`;
}

function formatGscWowDelta(delta) {
  if (delta == null) return '—';
  const n = Number(delta);
  if (n > 0) return `+${n}`;
  return String(n);
}

const initialForm = {
  name: '',
  email: '',
  phone: '',
  company: '',
  website: '',
  industry: '',
  city: '',
  state: '',
  description: '',
  target_audience: '',
  content_preferences: '',
  posting_schedule: '',
  brand_guidelines: '',
  brand_hashtags: '',
};

export default function Clients() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  const [showArchived, setShowArchived] = useState(false);
  const {
    clients,
    loading,
    error: clientsLoadError,
    refreshClients,
  } = useClients(showArchived);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [viewClient, setViewClient] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [detailsTab, setDetailsTab] = useState(0);
  const [linkedinStatus, setLinkedinStatus] = useState({});
  const [xStatus, setXStatus] = useState({});
  const [youtubeStatus, setYoutubeStatus] = useState({});
  const [tumblrStatus, setTumblrStatus] = useState({});
  const [pinterestStatus, setPinterestStatus] = useState({});
  const [instagramStatus, setInstagramStatus] = useState({});
  const [facebookConnected, setFacebookConnected] = useState(false);
  // Bumped after OAuth so FacebookPageSelector remounts and re-fetches the new page pool.
  const [facebookConnectNonce, setFacebookConnectNonce] = useState(0);

  const [archivingId, setArchivingId] = useState(null);

  const [instagramModalOpen, setInstagramModalOpen] = useState(false);
  const [instagramModalClient, setInstagramModalClient] = useState(null);
  const [instagramModalUsername, setInstagramModalUsername] = useState('');
  const [instagramModalPassword, setInstagramModalPassword] = useState('');
  const [instagramModalSubmitting, setInstagramModalSubmitting] = useState(false);

  const [gscSummaryByClient, setGscSummaryByClient] = useState({});
  const [gscPickerOpen, setGscPickerOpen] = useState(false);
  const [gscPickerClientId, setGscPickerClientId] = useState(null);
  const [gscPickerSites, setGscPickerSites] = useState([]);
  const [gscPickerBusy, setGscPickerBusy] = useState(false);
  const [readinessByClient, setReadinessByClient] = useState({});
  const [readinessLoadingByClient, setReadinessLoadingByClient] = useState({});
  const [readinessErrorByClient, setReadinessErrorByClient] = useState({});

  const fetchClientReadiness = useCallback(async (clientId) => {
    if (!clientId) return;
    setReadinessLoadingByClient((prev) => ({ ...prev, [clientId]: true }));
    setReadinessErrorByClient((prev) => ({ ...prev, [clientId]: null }));
    try {
      const data = await clientsAPI.getClientReadiness(clientId);
      setReadinessByClient((prev) => ({ ...prev, [clientId]: data }));
    } catch (e) {
      setReadinessErrorByClient((prev) => ({
        ...prev,
        [clientId]:
          e?.response?.data?.detail || e?.message || 'Failed to load readiness',
      }));
    } finally {
      setReadinessLoadingByClient((prev) => ({ ...prev, [clientId]: false }));
    }
  }, []);

  const fetchGscSummary = useCallback(async (clientId) => {
    try {
      const data = await gscAPI.getSummary(clientId);
      setGscSummaryByClient((prev) => ({ ...prev, [clientId]: data }));
    } catch {
      setGscSummaryByClient((prev) => ({
        ...prev,
        [clientId]: { connected: false },
      }));
    }
  }, []);

  // Full Facebook Login (new facebook_user token + page pool). "Refresh pages" only re-reads
  // pages with the existing grant, so newly requested scopes are never granted through it.
  const handleFacebookConnect = async () => {
    try {
      const url = await facebookAPI.getFacebookAuthUrl();
      window.open(url, '_blank', 'width=600,height=700');
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || 'Failed to start Facebook login');
    }
  };

  const handleGscConnect = async (clientId) => {
    try {
      const url = await gscAPI.getLoginUrl(clientId);
      window.open(url, '_blank', 'width=600,height=720');
    } catch (e) {
      showError(e?.message || 'Failed to start Google Search Console login');
    }
  };

  const handleGscDisconnect = async (clientId) => {
    try {
      await gscAPI.disconnect(clientId);
      await fetchGscSummary(clientId);
      await fetchClientReadiness(clientId);
      showSuccess('Search Console disconnected.');
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || 'Failed to disconnect');
    }
  };

  const handleGscSyncNow = async (clientId) => {
    try {
      const out = await gscAPI.syncNow(clientId);
      if (out.celery_task_id) {
        showSuccess(
          `Sync queued (task ${String(out.celery_task_id).slice(0, 8)}…). Updates run in the background.`
        );
      } else {
        showSuccess('Sync requested.');
      }
      await fetchClientReadiness(clientId);
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || 'Could not queue sync');
    }
  };

  const handleInstagramModalClose = () => {
    setInstagramModalOpen(false);
    setInstagramModalClient(null);
    setInstagramModalUsername('');
    setInstagramModalPassword('');
  };

  const handleInstagramConnectSubmit = async () => {
    const username = (instagramModalUsername || '').trim().replace(/^@/, '');
    if (!username) {
      showError('Please enter an Instagram username.');
      return;
    }
    const client = instagramModalClient;
    if (!client) return;
    setInstagramModalSubmitting(true);
    try {
      await instagramAPI.connectInstagramManually(
        client.id,
        username,
        instagramModalPassword || null
      );
      setInstagramStatus((prev) => ({ ...prev, [client.id]: true }));
      await fetchClientReadiness(client.id);
      showSuccess(
        instagramModalPassword
          ? `✅ Instagram @${username} connected with posting (browser login).`
          : `✅ Instagram account @${username} connected.`
      );
      handleInstagramModalClose();
    } catch (error) {
      showError(`❌ Failed to connect Instagram: ${error.message}`);
    } finally {
      setInstagramModalSubmitting(false);
    }
  };

  const fetchClients = useCallback(
    () => refreshClients({ includeArchived: showArchived, force: true }),
    [refreshClients, showArchived],
  );

  const fetchLinkedInStatus = async (clientId) => {
    try {
      const status = await linkedinAPI.getLinkedInStatus(clientId);
      setLinkedinStatus((prev) => ({ ...prev, [clientId]: status.connected }));
    } catch {
      setLinkedinStatus((prev) => ({ ...prev, [clientId]: false }));
    }
  };

  const fetchXStatus = async (clientId) => {
    try {
      const status = await xAPI.getXStatus(clientId);
      setXStatus((prev) => ({ ...prev, [clientId]: status.connected }));
    } catch {
      setXStatus((prev) => ({ ...prev, [clientId]: false }));
    }
  };

  const fetchYouTubeStatus = async (clientId) => {
    try {
      const status = await youtubeAPI.getYouTubeStatus(clientId);
      setYoutubeStatus((prev) => ({ ...prev, [clientId]: status.connected }));
    } catch {
      setYoutubeStatus((prev) => ({ ...prev, [clientId]: false }));
    }
  };

  const fetchTumblrStatus = async (clientId) => {
    try {
      const status = await tumblrAPI.getTumblrStatus(clientId);
      setTumblrStatus((prev) => ({ ...prev, [clientId]: status.connected }));
    } catch {
      setTumblrStatus((prev) => ({ ...prev, [clientId]: false }));
    }
  };

  const fetchPinterestStatus = async (clientId) => {
    try {
      const status = await pinterestAPI.getPinterestStatus(clientId);
      setPinterestStatus((prev) => ({ ...prev, [clientId]: status.connected }));
    } catch {
      setPinterestStatus((prev) => ({ ...prev, [clientId]: false }));
    }
  };

  const fetchInstagramStatus = async (clientId) => {
    try {
      const status = await instagramAPI.getInstagramStatus(clientId);
      setInstagramStatus((prev) => ({ ...prev, [clientId]: status.connected }));
    } catch {
      setInstagramStatus((prev) => ({ ...prev, [clientId]: false }));
    }
  };

  const checkFacebookConnection = async () => {
    try {
      const status = await facebookAPI.getFacebookStatus();
      setFacebookConnected(status.connected);
    } catch (error) {
      console.error('Failed to check Facebook connection:', error);
      setFacebookConnected(false);
    }
  };

  useEffect(() => {
    if (clients.length > 0) {
      clients.forEach((client) => {
        fetchLinkedInStatus(client.id);
        fetchXStatus(client.id);
        fetchPinterestStatus(client.id);
        fetchYouTubeStatus(client.id);
        fetchTumblrStatus(client.id);
        fetchGscSummary(client.id);
        if (SHOW_STANDALONE_INSTAGRAM_CONNECT) {
          fetchInstagramStatus(client.id);
        }
      });
    }
  }, [clients, fetchGscSummary]);

  useEffect(() => {
    if (viewClient?.id) {
      fetchGscSummary(viewClient.id);
      fetchClientReadiness(viewClient.id);
    }
  }, [viewClient?.id, fetchGscSummary, fetchClientReadiness]);

  // Listen for LinkedIn OAuth completion
  useEffect(() => {
    const refreshReadinessForMessage = (clientId) => {
      const cid = clientId ?? viewClient?.id;
      if (cid != null) fetchClientReadiness(cid);
    };

    function handleMessage(event) {
      if (event.data?.type === 'linkedin_connected') {
        fetchClients();
        refreshReadinessForMessage(event.data.client_id);
      } else if (event.data?.type === 'linkedin_error') {
        showError(`LinkedIn connection failed: ${event.data.error}`);
      } else if (event.data?.type === 'x_connected') {
        showSuccess('X (Twitter) connected!');
        fetchClients();
        refreshReadinessForMessage(event.data.client_id);
      } else if (event.data?.type === 'pinterest_connected') {
        showSuccess('Pinterest connected!');
        fetchClients();
        refreshReadinessForMessage(event.data.client_id);
        const clientIds = clients.map((c) => c.id);
        clientIds.forEach((id) => fetchPinterestStatus(id));
      } else if (event.data?.type === 'pinterest_error') {
        showError(`Pinterest connection failed: ${event.data.error || 'Could not save account'}`);
      } else if (event.data?.type === 'youtube_connected') {
        setTimeout(() => {
          fetchClients();
          refreshReadinessForMessage(event.data.client_id);
        }, 300);
      } else if (event.data?.type === 'youtube_error') {
        showError(`YouTube connection failed: ${event.data.error}`);
      } else if (event.data?.type === 'tumblr_connected') {
        fetchClients();
        refreshReadinessForMessage(event.data.client_id);
      } else if (event.data?.type === 'tumblr_error') {
        showError(`Tumblr connection failed: ${event.data.error}`);
      } else if (event.data?.type === 'instagram_connected') {
        fetchClients();
        refreshReadinessForMessage(event.data.client_id);
      } else if (event.data?.type === 'instagram_error') {
        showError(`Instagram connection failed: ${event.data.error}`);
      } else if (event.data?.type === 'facebook_connected') {
        showSuccess(
          'Facebook connected. Re-assign the Page below so this client picks up the new token.'
        );
        checkFacebookConnection();
        setFacebookConnectNonce((n) => n + 1);
        refreshReadinessForMessage(event.data.client_id);
      } else if (event.data?.type === 'facebook_error') {
        showError(`Facebook connection failed: ${event.data.error}`);
      } else if (event.data?.type === 'gsc_connected') {
        showSuccess('Google Search Console connected!');
        const cid = event.data.client_id;
        if (cid != null) fetchGscSummary(cid);
        refreshReadinessForMessage(cid);
      } else if (event.data?.type === 'gsc_error') {
        showError(
          event.data.detail ||
            event.data.error ||
            'Search Console connection failed'
        );
      } else if (
        event.data?.type === 'gsc_needs_preferred_site' ||
        event.data?.type === 'gsc_ambiguous_site_match'
      ) {
        setGscPickerClientId(event.data.client_id ?? null);
        setGscPickerSites(
          Array.isArray(event.data.sites) ? event.data.sites : []
        );
        setGscPickerOpen(true);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [clients, fetchClientReadiness, fetchGscSummary, showError, showSuccess, viewClient?.id]);

  useEffect(() => {
    fetchClients();
    checkFacebookConnection();
  }, []);

  const handleOpenForm = (client = null) => {
    if (client) {
      setForm({
        ...client,
        brand_hashtags: Array.isArray(client.brand_hashtags)
          ? client.brand_hashtags.join(', ')
          : '',
      });
      setEditingId(client.id);
    } else {
      setForm(initialForm);
      setEditingId(null);
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setForm(initialForm);
    setEditingId(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (typeof payload.brand_hashtags === 'string') {
        payload.brand_hashtags = payload.brand_hashtags
          .split(',')
          .map((s) => s.trim().replace(/^#/, ''))
          .filter(Boolean);
      }
      if (editingId) {
        await clientsAPI.updateClient(editingId, payload);
        showSuccess('Client updated successfully!');
        fetchClients();
        handleCloseForm();
      } else {
        const created = await clientsAPI.createClient(payload);
        showSuccess('Client created successfully!');
        await fetchClients();
        handleCloseForm();
        const newId = created?.id;
        if (newId != null) {
          navigate(`/clients?client_id=${newId}`);
          await handleView(newId, { platformTab: true });
        }
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || 'Failed to save client';
      setError(msg);
      showError(msg);
    }
  };

  const handleDelete = (id) => {
    const client = clients.find((c) => c.id === id);
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) return;

    setDeletingId(clientToDelete.id);
    setDeleteConfirmOpen(false);

    try {
      await clientsAPI.deleteClient(clientToDelete.id);
      showSuccess('Client deleted successfully!');
      fetchClients();
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || 'Failed to delete client';
      setError(msg);
      showError(msg);
    } finally {
      setDeletingId(null);
      setClientToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setClientToDelete(null);
  };

  const handleArchive = async (id) => {
    setArchivingId(id);
    try {
      await clientsAPI.archiveClient(id);
      showSuccess('Client archived successfully');
      fetchClients();
    } catch (err) {
      showError('Failed to archive client');
    } finally {
      setArchivingId(null);
    }
  };

  const handleUnarchive = async (id) => {
    setArchivingId(id);
    try {
      await clientsAPI.unarchiveClient(id);
      showSuccess('Client unarchived successfully');
      fetchClients();
    } catch (err) {
      showError('Failed to unarchive client');
    } finally {
      setArchivingId(null);
    }
  };

  const handleView = useCallback(async (id, { platformTab = false } = {}) => {
    if (platformTab) {
      setDetailsTab(1);
    }
    try {
      const client = await clientsAPI.getClient(id);
      setViewClient(client);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to load client details';
      setError(msg);
      showError(msg);
    }
  }, [showError]);

  const handleCloseView = () => {
    setViewClient(null);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const clientId = urlParams.get('client_id');
    if (!clientId) return;
    const id = parseInt(clientId, 10);
    if (Number.isFinite(id) && id > 0) {
      handleView(id);
    }
  }, [location.search, handleView]);

  const handleTabChange = (event, newValue) => {
    setDetailsTab(newValue);
  };

  const handleCampaignCreated = (campaignResult) => {
    // Show success message or update UI
    console.log('Campaign created:', campaignResult);
    // You could add a success notification here
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#0d1117' }}>
      <PageHeader
        title="Clients"
        subtitle="Manage your client relationships and social media accounts"
        icon={<Business sx={{ fontSize: 32, color: '#58a6ff' }} />}
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenForm()}
              sx={{
                bgcolor: '#238636',
                color: '#ffffff',
                '&:hover': { bgcolor: '#2ea043' },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(35, 134, 54, 0.3)',
              }}
            >
              Add Client
            </Button>
            <Button
              variant="outlined"
              startIcon={showArchived ? <Unarchive /> : <Archive />}
              onClick={() => setShowArchived(!showArchived)}
              sx={{
                borderColor: '#30363d',
                color: '#f0f6fc',
                '&:hover': {
                  borderColor: '#484f58',
                  bgcolor: '#21262d',
                },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </Button>
          </>
        }
        sx={{ mb: 2 }}
      />

      {(error || clientsLoadError) && (
        <Card sx={{ mb: 3, bgcolor: '#21262d', border: '1px solid #f85149' }}>
          <CardContent>
            <Typography color="#f85149" sx={{ fontWeight: 500 }}>
              {error || clientsLoadError}
            </Typography>
          </CardContent>
        </Card>
      )}

      <DataTable
        loading={loading}
        empty={!(Array.isArray(clients) && clients.length)}
        emptyMessage={showArchived ? 'No archived clients.' : 'No clients yet. Add your first client to get started.'}
      >
              <TableHead>
                <TableRow sx={{ bgcolor: '#21262d' }}>
                  <TableCell
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 600,
                      borderBottom: '1px solid #30363d',
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 600,
                      borderBottom: '1px solid #30363d',
                    }}
                  >
                    Company
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 600,
                      borderBottom: '1px solid #30363d',
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 600,
                      borderBottom: '1px solid #30363d',
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 600,
                      borderBottom: '1px solid #30363d',
                    }}
                  >
                    Industry
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 600,
                      borderBottom: '1px solid #30363d',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(clients) ? clients : []).map((client, index) => (
                  <TableRow
                    key={client.id}
                    sx={{
                      bgcolor:
                        index % 2 === 0
                          ? 'rgba(13, 17, 23, 0.6)'
                          : 'rgba(22, 27, 34, 0.6)',
                      '&:hover': { bgcolor: '#21262d' },
                      borderBottom: '1px solid #30363d',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <TableCell sx={{ color: '#f0f6fc', py: 2 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: '#58a6ff',
                            width: 32,
                            height: 32,
                            fontSize: 14,
                          }}
                        >
                          {client.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography sx={{ fontWeight: 500 }}>
                          {client.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#c9d1d9' }}>
                      {client.company}
                    </TableCell>
                    <TableCell sx={{ color: '#c9d1d9' }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Email sx={{ fontSize: 16, color: '#7d8590' }} />
                        {client.email}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#c9d1d9' }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Phone sx={{ fontSize: 16, color: '#7d8590' }} />
                        {client.phone}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#c9d1d9' }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Work sx={{ fontSize: 16, color: '#7d8590' }} />
                        {client.industry}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          p: 1,
                          maxWidth: { xs: 160, sm: 'none' },
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          border: '1px solid #30363d',
                        }}
                      >
                        <Tooltip title="Link Facebook Page">
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                minWidth: 44,
                                minHeight: 44,
                                bgcolor: '#1877f2',
                                color: '#ffffff',
                                '&:hover': { bgcolor: '#166fe5' },
                                transition: 'all 0.2s',
                              }}
                              onClick={() => {
                                handleView(client.id);
                                setDetailsTab(0);
                              }}
                            >
                              <Facebook fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {SHOW_STANDALONE_INSTAGRAM_CONNECT && (
                          <Tooltip
                            title={
                              instagramStatus[client.id]
                                ? 'Disconnect from Instagram'
                                : 'Link to Instagram'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                sx={{
                                  p: 1,
                                  bgcolor: instagramStatus[client.id]
                                    ? '#e1306c'
                                    : '#30363d',
                                  color: '#ffffff',
                                  '&:hover': {
                                    bgcolor: instagramStatus[client.id]
                                      ? '#c13584'
                                      : '#484f58',
                                  },
                                  transition: 'all 0.2s',
                                }}
                                onClick={async () => {
                                  if (instagramStatus[client.id]) {
                                    await instagramAPI.disconnectInstagram(
                                      client.id
                                    );
                                    setInstagramStatus((prev) => ({
                                      ...prev,
                                      [client.id]: false,
                                    }));
                                    showSuccess(
                                      'Instagram disconnected successfully!'
                                    );
                                  } else {
                                    setInstagramModalClient(client);
                                    setInstagramModalUsername('');
                                    setInstagramModalPassword('');
                                    setInstagramModalOpen(true);
                                  }
                                }}
                              >
                                <Instagram fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={
                            linkedinStatus[client.id]
                              ? 'Disconnect from LinkedIn'
                              : 'Link to LinkedIn'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: linkedinStatus[client.id]
                                  ? '#0a66c2'
                                  : '#30363d',
                                color: '#ffffff',
                                '&:hover': {
                                  bgcolor: linkedinStatus[client.id]
                                    ? '#004182'
                                    : '#484f58',
                                },
                                transition: 'all 0.2s',
                              }}
                              onClick={async () => {
                                if (linkedinStatus[client.id]) {
                                  await linkedinAPI.disconnectLinkedIn(
                                    client.id
                                  );
                                  setLinkedinStatus((prev) => ({
                                    ...prev,
                                    [client.id]: false,
                                  }));
                                  showSuccess(
                                    'LinkedIn disconnected successfully!'
                                  );
                                } else {
                                  const url =
                                    await linkedinAPI.getLinkedInAuthUrl(
                                      client.id
                                    );
                                  window.open(
                                    url,
                                    '_blank',
                                    'width=600,height=700'
                                  );
                                }
                              }}
                            >
                              <LinkedIn fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            xStatus[client.id]
                              ? 'Disconnect X'
                              : "Link X (Twitter) – log in in the browser. Prefer 'Phone, email, or username' (Google sign-in may be blocked)."
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: xStatus[client.id]
                                  ? '#1da1f2'
                                  : '#30363d',
                                color: '#ffffff',
                                '&:hover': {
                                  bgcolor: xStatus[client.id]
                                    ? '#1a91da'
                                    : '#484f58',
                                },
                                transition: 'all 0.2s',
                              }}
                              onClick={async () => {
                                if (xStatus[client.id]) {
                                  await xAPI.disconnectX(client.id);
                                  setXStatus((prev) => ({
                                    ...prev,
                                    [client.id]: false,
                                  }));
                                  showSuccess('X disconnected.');
                                } else {
                                  const url =
                                    await xAPI.getXPuppeteerStartUrl(
                                      client.id
                                    );
                                  window.open(
                                    url,
                                    '_blank',
                                    'width=600,height=750'
                                  );
                                }
                              }}
                            >
                              <Twitter fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            youtubeStatus[client.id]
                              ? 'Disconnect YouTube'
                              : 'Link YouTube Account'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: youtubeStatus[client.id]
                                  ? '#ff0000'
                                  : '#30363d',
                                color: '#ffffff',
                                '&:hover': {
                                  bgcolor: youtubeStatus[client.id]
                                    ? '#cc0000'
                                    : '#484f58',
                                },
                                transition: 'all 0.2s',
                              }}
                              onClick={async () => {
                                if (youtubeStatus[client.id]) {
                                  await youtubeAPI.disconnectYouTube(client.id);
                                  setYoutubeStatus((prev) => ({
                                    ...prev,
                                    [client.id]: false,
                                  }));
                                  showSuccess(
                                    'YouTube disconnected successfully!'
                                  );
                                } else {
                                  const url =
                                    await youtubeAPI.getYouTubeAuthUrl(
                                      client.id
                                    );
                                  window.open(
                                    url,
                                    '_blank',
                                    'width=600,height=700'
                                  );
                                }
                              }}
                            >
                              <YouTube fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            tumblrStatus[client.id]
                              ? 'Disconnect Tumblr'
                              : 'Link Tumblr Account'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: tumblrStatus[client.id]
                                  ? '#36465d'
                                  : '#30363d',
                                color: '#ffffff',
                                '&:hover': {
                                  bgcolor: tumblrStatus[client.id]
                                    ? '#2c3e50'
                                    : '#484f58',
                                },
                                transition: 'all 0.2s',
                              }}
                              onClick={async () => {
                                if (tumblrStatus[client.id]) {
                                  await tumblrAPI.disconnectTumblr(client.id);
                                  setTumblrStatus((prev) => ({
                                    ...prev,
                                    [client.id]: false,
                                  }));
                                  showSuccess(
                                    'Tumblr disconnected successfully!'
                                  );
                                } else {
                                  const url = await tumblrAPI.getTumblrAuthUrl(
                                    client.id
                                  );
                                  window.open(
                                    url,
                                    '_blank',
                                    'width=600,height=700'
                                  );
                                }
                              }}
                            >
                              <Article fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            pinterestStatus[client.id]
                              ? 'Disconnect Pinterest'
                              : 'Link Pinterest – log in in the browser once'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: pinterestStatus[client.id]
                                  ? '#E60023'
                                  : '#30363d',
                                color: '#ffffff',
                                '&:hover': {
                                  bgcolor: pinterestStatus[client.id]
                                    ? '#bd001c'
                                    : '#484f58',
                                },
                                transition: 'all 0.2s',
                              }}
                              onClick={async () => {
                                if (pinterestStatus[client.id]) {
                                  await pinterestAPI.disconnectPinterest(client.id);
                                  setPinterestStatus((prev) => ({
                                    ...prev,
                                    [client.id]: false,
                                  }));
                                  showSuccess('Pinterest disconnected.');
                                } else {
                                  const url =
                                    await pinterestAPI.getPinterestPuppeteerStartUrl(
                                      client.id
                                    );
                                  window.open(
                                    url,
                                    '_blank',
                                    'width=600,height=750'
                                  );
                                }
                              }}
                            >
                              <PinterestIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            gscSummaryByClient[client.id]?.connected
                              ? 'Google Search Console (connected — open client for details)'
                              : 'Connect Google Search Console'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: gscSummaryByClient[client.id]?.connected
                                  ? '#1a73e8'
                                  : '#30363d',
                                color: '#ffffff',
                                '&:hover': {
                                  bgcolor: gscSummaryByClient[client.id]?.connected
                                    ? '#1557b0'
                                    : '#484f58',
                                },
                                transition: 'all 0.2s',
                              }}
                              onClick={async () => {
                                const g = gscSummaryByClient[client.id];
                                if (g?.connected) {
                                  handleView(client.id);
                                  setDetailsTab(0);
                                } else {
                                  await handleGscConnect(client.id);
                                }
                              }}
                            >
                              <ManageSearch fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Media library">
                          <IconButton
                            size="small"
                            sx={{
                              p: 1,
                              bgcolor: '#238636',
                              color: '#ffffff',
                              '&:hover': { bgcolor: '#2ea043' },
                              transition: 'all 0.2s',
                            }}
                            onClick={() =>
                              navigate(`/gallery?client_id=${client.id}`)
                            }
                          >
                            <PhotoLibrary fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Brand & AI onboarding">
                          <IconButton
                            size="small"
                            sx={{
                              p: 1,
                              bgcolor: '#8957e5',
                              color: '#ffffff',
                              '&:hover': { bgcolor: '#a371f7' },
                              transition: 'all 0.2s',
                            }}
                            onClick={() =>
                              navigate(`/clients/${client.id}/onboarding`)
                            }
                          >
                            <SettingsSuggest fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            sx={{
                              p: 1,
                              bgcolor: '#58a6ff',
                              color: '#ffffff',
                              '&:hover': { bgcolor: '#1f6feb' },
                              transition: 'all 0.2s',
                            }}
                            onClick={() => handleView(client.id)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            sx={{
                              p: 1,
                              bgcolor: '#f78166',
                              color: '#ffffff',
                              '&:hover': { bgcolor: '#f0883e' },
                              transition: 'all 0.2s',
                            }}
                            onClick={() => handleOpenForm(client)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {client.is_active !== false ? (
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: '#7c3aed',
                                color: '#ffffff',
                                '&:hover': { bgcolor: '#6d28d9' },
                                transition: 'all 0.2s',
                              }}
                              onClick={() => handleArchive(client.id)}
                              disabled={archivingId === client.id}
                            >
                              <Archive fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Unarchive">
                            <IconButton
                              size="small"
                              sx={{
                                p: 1,
                                bgcolor: '#059669',
                                color: '#ffffff',
                                '&:hover': { bgcolor: '#047857' },
                                transition: 'all 0.2s',
                              }}
                              onClick={() => handleUnarchive(client.id)}
                              disabled={archivingId === client.id}
                            >
                              <Unarchive fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            sx={{
                              p: 1,
                              bgcolor: '#f85149',
                              color: '#ffffff',
                              '&:hover': { bgcolor: '#da3633' },
                              transition: 'all 0.2s',
                            }}
                            onClick={() => handleDelete(client.id)}
                            disabled={deletingId === client.id}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
      </DataTable>

      {/* Add/Edit Client Dialog */}
      <Dialog
        open={openForm}
        onClose={handleCloseForm}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: 3,
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#f0f6fc',
            borderBottom: '1px solid #30363d',
            py: 3,
            px: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: '#161b22',
          }}
        >
          <Box
            sx={{
              bgcolor: '#58a6ff',
              borderRadius: 3,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Business sx={{ color: '#ffffff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {editingId ? 'Edit Client' : 'Add New Client'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#7d8590' }}>
              {editingId
                ? 'Update client information and settings'
                : 'Create a new client profile'}
            </Typography>
          </Box>
        </DialogTitle>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <DialogContent
            sx={{ pt: 3, px: 4, pb: 2, bgcolor: '#0d1117', flex: 1, overflowY: 'auto' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                height: '100%',
                width: '100%',
              }}
            >
              {/* Basic Information Card */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Card
                  sx={{
                    bgcolor: '#161b22',
                    border: '1px solid #30363d',
                    borderRadius: 3,
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: '#238636',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Business sx={{ color: '#ffffff', fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: '#f0f6fc', fontWeight: 600 }}
                    >
                      Basic Information
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      flex: 1,
                      justifyContent: 'space-between',
                    }}
                  >
                    <TextField
                      label="Client Name *"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      fullWidth
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Website URL"
                      name="website"
                      value={form.website}
                      onChange={handleChange}
                      fullWidth
                      placeholder="https://example.com"
                      helperText="For SEO analysis and competitive comparison"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#7d8590',
                        },
                      }}
                    />

                    <TextField
                      label="Email Address"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      fullWidth
                      type="email"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Phone Number"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Industry"
                      name="industry"
                      value={form.industry}
                      onChange={handleChange}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                      <TextField
                        label="City"
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#21262d',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#30363d' },
                            '&:hover fieldset': { borderColor: '#58a6ff' },
                            '&.Mui-focused fieldset': {
                              borderColor: '#58a6ff',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#7d8590',
                            fontWeight: 500,
                          },
                          '& .MuiInputBase-input': {
                            color: '#f0f6fc',
                            fontSize: '0.95rem',
                          },
                        }}
                      />

                      <TextField
                        label="State"
                        name="state"
                        value={form.state}
                        onChange={handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#21262d',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#30363d' },
                            '&:hover fieldset': { borderColor: '#58a6ff' },
                            '&.Mui-focused fieldset': {
                              borderColor: '#58a6ff',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#7d8590',
                            fontWeight: 500,
                          },
                          '& .MuiInputBase-input': {
                            color: '#f0f6fc',
                            fontSize: '0.95rem',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Card>
              </Box>

              {/* Right Column - Campaign Settings and Additional Details */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  height: '100%',
                }}
              >
                {/* Campaign Settings Card */}
                <Card
                  sx={{
                    bgcolor: '#161b22',
                    border: '1px solid #30363d',
                    borderRadius: 3,
                    p: 3,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: '#f78166',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Work sx={{ color: '#ffffff', fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: '#f0f6fc', fontWeight: 600 }}
                    >
                      Campaign Settings
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <TextField
                      label="Target Audience"
                      name="target_audience"
                      value={form.target_audience}
                      onChange={handleChange}
                      fullWidth
                      placeholder="e.g., Small business owners, 25-45"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Content Preferences"
                      name="content_preferences"
                      value={form.content_preferences}
                      onChange={handleChange}
                      fullWidth
                      placeholder="e.g., Educational, promotional, behind-the-scenes"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Posting Schedule"
                      name="posting_schedule"
                      value={form.posting_schedule}
                      onChange={handleChange}
                      fullWidth
                      placeholder="e.g., Mon, Wed, Fri at 9 AM"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />
                  </Box>
                </Card>

                {/* Additional Details Card */}
                <Card
                  sx={{
                    bgcolor: '#161b22',
                    border: '1px solid #30363d',
                    borderRadius: 3,
                    p: 3,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: '#58a6ff',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Article sx={{ color: '#ffffff', fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: '#f0f6fc', fontWeight: 600 }}
                    >
                      Additional Details
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <TextField
                      label="Description"
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Brief description of the client and their business..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Brand hashtags (for caption strategy)"
                      name="brand_hashtags"
                      value={form.brand_hashtags}
                      onChange={handleChange}
                      fullWidth
                      placeholder="e.g. DigitalDynamicSolution, YourBrand (comma-separated)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />

                    <TextField
                      label="Brand Guidelines"
                      name="brand_guidelines"
                      value={form.brand_guidelines}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Brand voice, tone, and style guidelines..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#21262d',
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#58a6ff',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7d8590',
                          fontWeight: 500,
                        },
                        '& .MuiInputBase-input': {
                          color: '#f0f6fc',
                          fontSize: '0.95rem',
                        },
                      }}
                    />
                  </Box>
                </Card>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              p: 3,
              borderTop: '1px solid #30363d',
              gap: 2,
              bgcolor: '#161b22',
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleCloseForm}
              sx={{
                color: '#7d8590',
                px: 4,
                py: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                '&:hover': {
                  bgcolor: '#21262d',
                  color: '#f0f6fc',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: '#238636',
                color: '#ffffff',
                '&:hover': { bgcolor: '#2ea043' },
                px: 5,
                py: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(35, 134, 54, 0.3)',
              }}
            >
              {editingId ? 'Update Client' : 'Add Client'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Client Details Dialog */}
      <Dialog
        open={!!viewClient}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#161b22',
            border: '1px solid #30363d',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ color: '#f0f6fc', borderBottom: '1px solid #30363d' }}
        >
          Client Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewClient && (
            <Box>
              <Tabs
                value={detailsTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 2,
                  '& .MuiTab-root': {
                    color: '#7d8590',
                    '&.Mui-selected': { color: '#58a6ff' },
                  },
                  '& .MuiTabs-indicator': { bgcolor: '#58a6ff' },
                }}
              >
                <Tab label="Info" />
                <Tab label="Platform Accounts" />
              </Tabs>
              {detailsTab === 0 && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <ClientReadinessPanel
                      readiness={readinessByClient[viewClient.id]}
                      loading={Boolean(readinessLoadingByClient[viewClient.id])}
                      error={readinessErrorByClient[viewClient.id]}
                      onRefresh={() => fetchClientReadiness(viewClient.id)}
                    />
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}
                      >
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#f0f6fc',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Business sx={{ color: '#58a6ff' }} />
                            Basic Information
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Name
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc', fontWeight: 500 }}
                              >
                                {viewClient.name}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Company
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc', fontWeight: 500 }}
                              >
                                {viewClient.company}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Industry
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc', fontWeight: 500 }}
                              >
                                {viewClient.industry}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Status
                              </Typography>
                              <Chip
                                label={
                                  viewClient.is_active ? 'Active' : 'Inactive'
                                }
                                color={
                                  viewClient.is_active ? 'success' : 'default'
                                }
                                size="small"
                                sx={{
                                  bgcolor: viewClient.is_active
                                    ? '#238636'
                                    : '#30363d',
                                  color: '#ffffff',
                                  fontWeight: 500,
                                }}
                              />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}
                      >
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#f0f6fc',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Email sx={{ color: '#58a6ff' }} />
                            Contact Information
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Email
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc', fontWeight: 500 }}
                              >
                                {viewClient.email}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Phone
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc', fontWeight: 500 }}
                              >
                                {viewClient.phone}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card
                        sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}
                      >
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#f0f6fc',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Work sx={{ color: '#58a6ff' }} />
                            Campaign Details
                          </Typography>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#7d8590', mb: 0.5 }}
                                >
                                  Description
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: '#f0f6fc' }}
                                >
                                  {viewClient.description ||
                                    'No description provided'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#7d8590', mb: 0.5 }}
                                >
                                  Target Audience
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: '#f0f6fc' }}
                                >
                                  {viewClient.target_audience ||
                                    'Not specified'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#7d8590', mb: 0.5 }}
                                >
                                  Content Preferences
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: '#f0f6fc' }}
                                >
                                  {viewClient.content_preferences ||
                                    'Not specified'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#7d8590', mb: 0.5 }}
                                >
                                  Posting Schedule
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: '#f0f6fc' }}
                                >
                                  {viewClient.posting_schedule ||
                                    'Not specified'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#7d8590', mb: 0.5 }}
                                >
                                  Brand Guidelines
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: '#f0f6fc' }}
                                >
                                  {viewClient.brand_guidelines ||
                                    'No brand guidelines provided'}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card
                        sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}
                      >
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{ color: '#f0f6fc', mb: 2 }}
                          >
                            Timestamps
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 4 }}>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Created
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc' }}
                              >
                                {viewClient.created_at}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Last Updated
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc' }}
                              >
                                {viewClient.updated_at}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card
                        sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}
                      >
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{ color: '#f0f6fc', mb: 2 }}
                          >
                            Google Search Console
                          </Typography>
                          {!gscSummaryByClient[viewClient.id] ? (
                            <Box sx={{ py: 2 }}>
                              <CircularProgress size={28} />
                            </Box>
                          ) : !gscSummaryByClient[viewClient.id].connected ? (
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 2 }}
                              >
                                Connect Search Console to sync search analytics for
                                this workspace (same Google OAuth popup flow as
                                YouTube).
                              </Typography>
                              <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={() => handleGscConnect(viewClient.id)}
                                sx={{
                                  bgcolor: '#1a73e8',
                                  '&:hover': { bgcolor: '#1557b0' },
                                  textTransform: 'none',
                                  minHeight: 44,
                                  maxWidth: { sm: 280 },
                                }}
                              >
                                Connect Search Console
                              </Button>
                            </Box>
                          ) : (
                            <Box>
                              <Chip
                                label="Connected"
                                color="success"
                                size="small"
                                sx={{ mb: 2 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Property
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc', mb: 1.5 }}
                              >
                                {gscSummaryByClient[viewClient.id].site_url || '—'}
                              </Typography>
                              {gscSummaryByClient[viewClient.id]
                                .connected_google_email && (
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#7d8590', mb: 0.5 }}
                                  >
                                    Google account
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ color: '#f0f6fc', mb: 1.5 }}
                                  >
                                    {
                                      gscSummaryByClient[viewClient.id]
                                        .connected_google_email
                                    }
                                  </Typography>
                                </>
                              )}
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Last sync
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ color: '#f0f6fc', mb: 2 }}
                              >
                                {gscSummaryByClient[viewClient.id].last_sync_at
                                  ? new Date(
                                      gscSummaryByClient[viewClient.id]
                                        .last_sync_at
                                    ).toLocaleString()
                                  : 'Never'}
                              </Typography>
                              {gscSummaryByClient[viewClient.id].last_error && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#f78166', mb: 2 }}
                                >
                                  Last error:{' '}
                                  {String(
                                    gscSummaryByClient[viewClient.id].last_error
                                  ).slice(0, 500)}
                                </Typography>
                              )}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Button
                                  variant="contained"
                                  size="large"
                                  sx={{
                                    textTransform: 'none',
                                    minHeight: 44,
                                    bgcolor: '#1a73e8',
                                    '&:hover': { bgcolor: '#1557b0' },
                                  }}
                                  onClick={() =>
                                    handleGscConnect(viewClient.id)
                                  }
                                >
                                  Reconnect
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="large"
                                  sx={{ textTransform: 'none', minHeight: 44 }}
                                  onClick={() => handleGscSyncNow(viewClient.id)}
                                >
                                  Sync now
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="large"
                                  sx={{ textTransform: 'none', minHeight: 44 }}
                                  onClick={() =>
                                    handleGscDisconnect(viewClient.id)
                                  }
                                >
                                  Disconnect
                                </Button>
                              </Box>
                              {(() => {
                                const g = gscSummaryByClient[viewClient.id];
                                const hasWindow =
                                  g.period_start && g.period_end;
                                const topQ = g.top_queries_by_clicks || [];
                                const topP = g.top_pages_by_impressions || [];
                                const wowUp = g.wow_rising_queries || [];
                                const wowDown = g.wow_falling_queries || [];
                                const opps =
                                  g.high_impression_low_ctr_pages || [];
                                return (
                                  <Box sx={{ mt: 3 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ color: '#f0f6fc', mb: 0.5 }}
                                    >
                                      Search performance
                                    </Typography>
                                    {hasWindow ? (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#7d8590',
                                          display: 'block',
                                          mb: 2,
                                        }}
                                      >
                                        Reporting window:{' '}
                                        {new Date(
                                          `${g.period_start}T12:00:00`
                                        ).toLocaleDateString()}{' '}
                                        –{' '}
                                        {new Date(
                                          `${g.period_end}T12:00:00`
                                        ).toLocaleDateString()}{' '}
                                        (latest synced 7-day period)
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{ color: '#7d8590', mb: 2 }}
                                      >
                                        No search metrics yet. Run a sync after
                                        data appears in Search Console.
                                      </Typography>
                                    )}
                                    {opps.length > 0 && (
                                      <Alert
                                        severity="info"
                                        sx={{
                                          mb: 2,
                                          bgcolor: 'rgba(56, 139, 253, 0.1)',
                                          color: '#c9d1d9',
                                          border: '1px solid #30363d',
                                          '& .MuiAlert-icon': {
                                            color: '#58a6ff',
                                          },
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ mb: 0.5, color: '#f0f6fc' }}
                                        >
                                          Opportunities
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          These URLs get strong impressions but
                                          low CTR — titles or meta descriptions
                                          may be worth a refresh.
                                        </Typography>
                                        <Box
                                          component="ul"
                                          sx={{
                                            m: 0,
                                            pl: 2.5,
                                            '& li': { mb: 0.5 },
                                          }}
                                        >
                                          {opps.map((row) => (
                                            <Typography
                                              key={row.page}
                                              component="li"
                                              variant="body2"
                                              sx={{
                                                wordBreak: 'break-all',
                                              }}
                                            >
                                              {row.page}{' '}
                                              <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{ color: '#8b949e' }}
                                              >
                                                ({row.impressions} impr · CTR{' '}
                                                {formatGscCtrRatio(row.ctr)}
                                                {row.position != null
                                                  ? ` · pos ${Number(row.position).toFixed(1)}`
                                                  : ''}
                                                )
                                              </Typography>
                                            </Typography>
                                          ))}
                                        </Box>
                                      </Alert>
                                    )}
                                    {hasWindow && topQ.length > 0 && (
                                      <>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: '#8b949e',
                                            mb: 1,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Top queries
                                        </Typography>
                                        <TableContainer
                                          component={Paper}
                                          sx={{
                                            mb: 3,
                                            bgcolor: '#161b22',
                                            border: '1px solid #30363d',
                                          }}
                                        >
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Query
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Clicks
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Impr.
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  CTR
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Pos.
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  WoW Δ clicks
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {topQ.map((row) => (
                                                <TableRow key={row.query}>
                                                  <TableCell
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {row.query}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {row.clicks}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {row.impressions}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {formatGscCtrRatio(
                                                      row.ctr
                                                    )}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {row.position != null
                                                      ? Number(
                                                          row.position
                                                        ).toFixed(1)
                                                      : '—'}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{
                                                      color: '#f0f6fc',
                                                    }}
                                                  >
                                                    {formatGscWowDelta(
                                                      row.click_delta_vs_prior_week
                                                    )}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </>
                                    )}
                                    {hasWindow &&
                                      (wowUp.length > 0 ||
                                        wowDown.length > 0) && (
                                      <>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: '#8b949e',
                                            mb: 1,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Week-over-week (vs prior 7 days)
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#7d8590',
                                            display: 'block',
                                            mb: 1.5,
                                          }}
                                        >
                                          Prior window:{' '}
                                          {g.prior_week_start && g.prior_week_end
                                            ? `${new Date(`${g.prior_week_start}T12:00:00`).toLocaleDateString()} – ${new Date(`${g.prior_week_end}T12:00:00`).toLocaleDateString()}`
                                            : '—'}
                                        </Typography>
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                          <Grid item xs={12} md={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mb: 1,
                                              }}
                                            >
                                              <TrendingUp
                                                sx={{
                                                  fontSize: 18,
                                                  color: '#3fb950',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: '#8b949e',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                Top rising (clicks)
                                              </Typography>
                                            </Box>
                                            <TableContainer
                                              component={Paper}
                                              sx={{
                                                bgcolor: '#161b22',
                                                border: '1px solid #30363d',
                                              }}
                                            >
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Query
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Prior
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Current
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Δ
                                                    </TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {wowUp.map((row) => (
                                                    <TableRow key={row.query}>
                                                      <TableCell
                                                        sx={{
                                                          color: '#f0f6fc',
                                                        }}
                                                      >
                                                        {row.query}
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          color: '#f0f6fc',
                                                        }}
                                                      >
                                                        {row.prior_week_clicks}
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          color: '#f0f6fc',
                                                        }}
                                                      >
                                                        {row.clicks}
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          color: '#3fb950',
                                                          fontWeight: 600,
                                                        }}
                                                      >
                                                        +
                                                        {row.click_delta}
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Grid>
                                          <Grid item xs={12} md={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mb: 1,
                                              }}
                                            >
                                              <TrendingDown
                                                sx={{
                                                  fontSize: 18,
                                                  color: '#f85149',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: '#8b949e',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                Top falling (clicks)
                                              </Typography>
                                            </Box>
                                            <TableContainer
                                              component={Paper}
                                              sx={{
                                                bgcolor: '#161b22',
                                                border: '1px solid #30363d',
                                              }}
                                            >
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Query
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Prior
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Current
                                                    </TableCell>
                                                    <TableCell
                                                      align="right"
                                                      sx={{ color: '#8b949e' }}
                                                    >
                                                      Δ
                                                    </TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {wowDown.map((row) => (
                                                    <TableRow key={row.query}>
                                                      <TableCell
                                                        sx={{
                                                          color: '#f0f6fc',
                                                        }}
                                                      >
                                                        {row.query}
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          color: '#f0f6fc',
                                                        }}
                                                      >
                                                        {row.prior_week_clicks}
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          color: '#f0f6fc',
                                                        }}
                                                      >
                                                        {row.clicks}
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          color: '#f85149',
                                                          fontWeight: 600,
                                                        }}
                                                      >
                                                        {row.click_delta}
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Grid>
                                        </Grid>
                                      </>
                                    )}
                                    {hasWindow && topP.length > 0 && (
                                      <>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: '#8b949e',
                                            mb: 1,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Top pages
                                        </Typography>
                                        <TableContainer
                                          component={Paper}
                                          sx={{
                                            bgcolor: '#161b22',
                                            border: '1px solid #30363d',
                                          }}
                                        >
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Page
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Clicks
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Impr.
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  CTR
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ color: '#8b949e' }}
                                                >
                                                  Pos.
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {topP.map((row) => (
                                                <TableRow key={row.page}>
                                                  <TableCell
                                                    sx={{
                                                      color: '#f0f6fc',
                                                      maxWidth: 360,
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                    }}
                                                    title={row.page}
                                                  >
                                                    {row.page}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {row.clicks}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {row.impressions}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {formatGscCtrRatio(
                                                      row.ctr
                                                    )}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{ color: '#f0f6fc' }}
                                                  >
                                                    {row.position != null
                                                      ? Number(
                                                          row.position
                                                        ).toFixed(1)
                                                      : '—'}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </>
                                    )}
                                  </Box>
                                );
                              })()}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <GbpConnectionCard
                        clientId={viewClient.id}
                        onChanged={() => fetchClientReadiness(viewClient.id)}
                      />
                    </Grid>
                  </Grid>

                  {facebookConnected ? (
                    <Box sx={{ mt: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#7d8590' }}>
                          Reconnect after revoking the app on Facebook or when new
                          permissions are needed, then re-assign the Page.
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleFacebookConnect}
                          sx={{ textTransform: 'none', borderColor: '#1877f2', color: '#58a6ff' }}
                        >
                          Reconnect Facebook
                        </Button>
                      </Box>
                      <FacebookPageSelector
                        key={facebookConnectNonce}
                        client={viewClient}
                        onUpdate={fetchClients}
                      />
                    </Box>
                  ) : (
                    <Card
                      sx={{
                        mt: 3,
                        bgcolor: '#21262d',
                        border: '1px solid #f78166',
                      }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#f78166' }}>
                          Facebook is not connected for your account. Connect it,
                          then assign this client&apos;s Page.
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleFacebookConnect}
                          sx={{ textTransform: 'none', bgcolor: '#1877f2' }}
                        >
                          Connect Facebook
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Quick Actions */}
                  <Card
                    sx={{
                      mt: 3,
                      bgcolor: '#21262d',
                      border: '1px solid #30363d',
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 2 }}>
                        Quick Actions
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: '#8957e5',
                            color: '#ffffff',
                            '&:hover': { bgcolor: '#a371f7' },
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                          }}
                          onClick={() => {
                            handleCloseView();
                            navigate(`/clients/${viewClient.id}/onboarding`);
                          }}
                        >
                          Brand & AI onboarding
                        </Button>
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: '#238636',
                            color: '#ffffff',
                            '&:hover': { bgcolor: '#2ea043' },
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                          }}
                          onClick={() => {
                            handleCloseView();
                            navigate(
                              `/posts?client_id=${encodeURIComponent(viewClient.id)}`
                            );
                          }}
                        >
                          Create Post for {viewClient.name}
                        </Button>
                        {viewClient.facebook_page_name && (
                          <Button
                            variant="outlined"
                            sx={{
                              borderColor: '#58a6ff',
                              color: '#58a6ff',
                              '&:hover': {
                                borderColor: '#1f6feb',
                                bgcolor: 'rgba(88, 166, 255, 0.1)',
                              },
                              px: 3,
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                            }}
                            onClick={() => {
                              handleCloseView();
                              navigate(
                                `/posts?client_id=${encodeURIComponent(viewClient.id)}&platform=facebook`
                              );
                            }}
                          >
                            Create Facebook Post
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
              {detailsTab === 1 && (
                <PlatformAccountsSection
                  clientId={viewClient.id}
                  onAccountsChanged={() => fetchClientReadiness(viewClient.id)}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #30363d' }}>
          <Button
            onClick={handleCloseView}
            sx={{
              color: '#7d8590',
              '&:hover': { bgcolor: '#21262d' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={gscPickerOpen}
        onClose={() => !gscPickerBusy && setGscPickerOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#161b22', color: '#f0f6fc' } }}
      >
        <DialogTitle sx={{ color: '#f0f6fc' }}>
          Choose Search Console property
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#7d8590', mb: 2 }}>
            This Google account has more than one property for this workspace.
            Pick the exact site URL to store (must match Search Console).
          </Typography>
          <List dense disablePadding>
            {gscPickerSites.map((site) => (
              <ListItemButton
                key={site}
                disabled={gscPickerBusy}
                onClick={async () => {
                  if (gscPickerClientId == null) return;
                  setGscPickerBusy(true);
                  try {
                    await gscAPI.selectProperty(gscPickerClientId, site);
                    showSuccess('Search Console property saved.');
                    setGscPickerOpen(false);
                    await fetchGscSummary(gscPickerClientId);
                  } catch (e) {
                    showError(
                      e?.response?.data?.detail ||
                        e?.message ||
                        'Could not save property'
                    );
                  } finally {
                    setGscPickerBusy(false);
                  }
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': { bgcolor: '#21262d' },
                }}
              >
                <ListItemText
                  primary={site}
                  primaryTypographyProps={{
                    sx: { color: '#f0f6fc', fontSize: '0.9rem' },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setGscPickerOpen(false)}
            disabled={gscPickerBusy}
            sx={{ color: '#7d8590' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connect Instagram Modal */}
      <Dialog
        open={SHOW_STANDALONE_INSTAGRAM_CONNECT && instagramModalOpen}
        onClose={handleInstagramModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#0d1117', color: '#f0f6fc' }}>
          Connect Instagram
          {instagramModalClient && (
            <Typography component="span" sx={{ fontWeight: 400, color: '#7d8590', ml: 1 }}>
              for {instagramModalClient.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: 3, pb: 2, bgcolor: '#0d1117' }}>
          <Typography sx={{ color: '#7d8590', mb: 2, fontSize: '0.875rem' }}>
            Use the client&apos;s Instagram credentials. Password is optional; include it to enable
            browser-based posting (same login flow as scraping).
          </Typography>
          <TextField
            fullWidth
            label="Instagram username"
            placeholder="username (without @)"
            value={instagramModalUsername}
            onChange={(e) => setInstagramModalUsername(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#161b22',
                color: '#f0f6fc',
                '& fieldset': { borderColor: '#30363d' },
                '&:hover fieldset': { borderColor: '#484f58' },
              },
              '& .MuiInputLabel-root': { color: '#7d8590' },
            }}
          />
          <TextField
            fullWidth
            type="password"
            label="Instagram password (optional)"
            placeholder="For browser posting; leave blank to connect without posting"
            value={instagramModalPassword}
            onChange={(e) => setInstagramModalPassword(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#161b22',
                color: '#f0f6fc',
                '& fieldset': { borderColor: '#30363d' },
                '&:hover fieldset': { borderColor: '#484f58' },
              },
              '& .MuiInputLabel-root': { color: '#7d8590' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#0d1117', borderTop: '1px solid #30363d' }}>
          <Button
            onClick={handleInstagramModalClose}
            sx={{ color: '#7d8590', '&:hover': { bgcolor: '#21262d' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInstagramConnectSubmit}
            variant="contained"
            disabled={instagramModalSubmitting || !(instagramModalUsername || '').trim()}
            sx={{
              bgcolor: '#e1306c',
              '&:hover': { bgcolor: '#c13584' },
            }}
          >
            {instagramModalSubmitting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#0d1117', color: '#f0f6fc' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: '#0d1117' }}>
          <Typography sx={{ color: '#f0f6fc' }}>
            Are you sure you want to delete{' '}
            <strong>{clientToDelete?.name}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ p: 3, bgcolor: '#0d1117', borderTop: '1px solid #30363d' }}
        >
          <Button
            onClick={cancelDelete}
            sx={{
              color: '#7d8590',
              '&:hover': { bgcolor: '#21262d' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={deletingId === clientToDelete?.id}
            sx={{
              bgcolor: '#da3633',
              '&:hover': { bgcolor: '#b02a28' },
            }}
          >
            {deletingId === clientToDelete?.id ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
